import { v4 as uuid } from "uuid";

import * as EntitySocialCommitment from "@entities/socialCommitment";

export type ProjectSocialCommitment =
  EntitySocialCommitment.ProjectSocialCommitment;
export type PackageSocialCommitment =
  EntitySocialCommitment.PackageSocialCommitment;

export enum TargetUnit {
  Spend = "target-type_spend",
  Hour = "target-type_hour",
}

export const targetTypeToString = (targetType: TargetUnit): string => {
  switch (targetType) {
    case TargetUnit.Spend:
      return "Spend";
    case TargetUnit.Hour:
      return "Hours";
  }
};

export enum SocialCommitmentTypeSpend {
  DefaultSpend = "social-commitment-type_default_spend",
  LocalContent = "social-commitment-type_local-content",
  SocialDisabilityEnterpriseSpend = "social-commitment-type_social-disability-enterprise-spend",
  AboriginalTorresStraitIslanderBusinessSpend = "social-commitment-type_aboriginal-torres-strait-island-buseinss-spend",
}

export const socialCommitmentTypeSpendToString = (
  socialCommitmentType: SocialCommitmentTypeSpend
): string => {
  switch (socialCommitmentType) {
    case SocialCommitmentTypeSpend.DefaultSpend:
      return "Default Spend";
    case SocialCommitmentTypeSpend.LocalContent:
      return "Local Content";
    case SocialCommitmentTypeSpend.SocialDisabilityEnterpriseSpend:
      return "Social Disability Enterprise Spend";
    case SocialCommitmentTypeSpend.AboriginalTorresStraitIslanderBusinessSpend:
      return "Aboriginal Torres-Strait Islander Business Spend";
  }
};

export enum SocialCommitmentTypeHour {
  DefaultHour = "social-commitment-type_default-hours",
  LocalJobsFirst = "social-commitment-type_local-jobs-first",
  MPSGHoursRequirement = "social-commitment-mpsg-hours-requirment",
  OpportunitiesForDisadvantagedVictorians = "social-commitment-type_opportunities-for-disadvantaged-victorians",
  FemaleParticipation = "social-commitment-type_female-participation",
  AboriginalTorresStraitIslanderParticipation = "social-commitment-type_aboriginal-torres-strait-island-participation",
}

export const socialCommitmentTypeHourToString = (
  socialCommitmentType: SocialCommitmentTypeHour
): string => {
  switch (socialCommitmentType) {
    case SocialCommitmentTypeHour.DefaultHour:
      return "Default Spend";
    case SocialCommitmentTypeHour.LocalJobsFirst:
      return "Local Jobs First";
    case SocialCommitmentTypeHour.MPSGHoursRequirement:
      return "MPSG Hours Requirement";
    case SocialCommitmentTypeHour.OpportunitiesForDisadvantagedVictorians:
      return "Opportunities For Disadvantaged Victorians";
    case SocialCommitmentTypeHour.FemaleParticipation:
      return "Female Participation";
    case SocialCommitmentTypeHour.AboriginalTorresStraitIslanderParticipation:
      return "Aboriginal Torres-Strait Islander Participation";
  }
};

export const socialCommitmentTypeToString = (
  targetName: SocialCommitmentTypeSpend | SocialCommitmentTypeHour
): string => {
  switch (targetName) {
    case SocialCommitmentTypeHour.DefaultHour:
      return "Default Hour";
    case SocialCommitmentTypeHour.LocalJobsFirst:
      return "Local Jobs First";
    case SocialCommitmentTypeHour.MPSGHoursRequirement:
      return "MPSG Hours Requirement";
    case SocialCommitmentTypeHour.OpportunitiesForDisadvantagedVictorians:
      return "Opportunities For Disadvantaged Victorians";
    case SocialCommitmentTypeHour.FemaleParticipation:
      return "Female Participation";
    case SocialCommitmentTypeHour.AboriginalTorresStraitIslanderParticipation:
      return "Aboriginal Torres-Strait Islander Participation";
    case SocialCommitmentTypeSpend.DefaultSpend:
      return "Default Spend";
    case SocialCommitmentTypeSpend.LocalContent:
      return "Local Content";
    case SocialCommitmentTypeSpend.SocialDisabilityEnterpriseSpend:
      return "Social Disability Enterprise Spend";
    case SocialCommitmentTypeSpend.AboriginalTorresStraitIslanderBusinessSpend:
      return "Aboriginal Torres-Strait Islander Business Spend";
  }
};

export type SocialCommitmentLineItem = {
  id: string;
  targetName: SocialCommitmentTypeSpend | SocialCommitmentTypeHour;
  targetUnit: TargetUnit;
  targetDescription?: string;
  targetValue: number;
  created: Date;
};

export const createDefaultSocialCommitmentLineItem = (
  targetUnit: TargetUnit
): SocialCommitmentLineItem => {
  const targetName =
    targetUnit === TargetUnit.Spend
      ? SocialCommitmentTypeSpend.DefaultSpend
      : SocialCommitmentTypeHour.DefaultHour;
  return {
    id: uuid(),
    targetName: targetName,
    targetUnit: targetUnit,
    targetValue: 0,
    created: new Date(),
  };
};

export const createDefaultCommitments = (
  defaultValue = 0
): SocialCommitmentLineItem[] => {
  const spendOptions = Object.values(SocialCommitmentTypeSpend).reduce(
    (acc, sc) => {
      if (sc !== SocialCommitmentTypeSpend.DefaultSpend) {
        return [
          ...acc,
          {
            id: uuid(),
            targetName: sc,
            targetUnit: TargetUnit.Spend,
            targetValue: defaultValue,
            created: new Date(),
          },
        ];
      }
      return [...acc];
    },
    [] as SocialCommitmentLineItem[]
  );

  const hoursOptions = Object.values(SocialCommitmentTypeHour).reduce(
    (acc, sc) => {
      if (sc !== SocialCommitmentTypeHour.DefaultHour) {
        return [
          ...acc,
          {
            id: uuid(),
            targetName: sc,
            targetUnit: TargetUnit.Hour,
            targetValue: defaultValue,
            created: new Date(),
          },
        ];
      }

      return [...acc];
    },
    [] as SocialCommitmentLineItem[]
  );
  return [...spendOptions, ...hoursOptions];
};

export const cloneCommitments = (
  commitments: SocialCommitmentLineItem[]
): SocialCommitmentLineItem[] => {
  return commitments.map((c) => {
    return { ...c, id: uuid(), targetValue: 0 };
  });
};
